import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import { updateFcmToken } from '@connect-x-app/hybrid-data-access';
import {
  useAuthenticationStore,
  useChannelSessionStore,
  useEnvConfigSessionStore,
  useFlightRoomSessionStore,
} from '@connect-x-app/hybrid-state';
import { useNotificationSound } from '@connect-x-app/hybrid-utils';
import { buildFlightRoomPushUrl, getPushNotificationPayload } from '@connect-x-app/shared-utils';
import { version } from '@connect-x-app/version';
import { getAnalytics as getGoogleAnalytics } from 'firebase/analytics';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

import { useFirebaseConfigSessionStore } from '../stores';

export const useFirebaseConfigInitializer = () => {
  const firebaseScope = '/firebase-push-notification-scope';
  const { env } = useEnvConfigSessionStore();
  const { playSound } = useNotificationSound();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const initializedConfigRef = useRef<string | null>(null);
  const isTokenRequestedRef = useRef(false);

  const { messaging, serviceWorker, setAnalytics, setMessaging, setServiceWorker } = useFirebaseConfigSessionStore();

  const firebaseConfig = useMemo(
    () => ({
      apiKey: env?.firebaseApiKey ?? '',
      appId: env?.firebaseAppId ?? '',
      authDomain: env?.firebaseAuthDomain ?? '',
      messagingSenderId: env?.firebaseMessagingSenderId ?? '',
      projectId: env?.firebaseProjectId ?? '',
      storageBucket: env?.firebaseStorageBucket ?? '',
    }),
    [env],
  );

  useEffect(() => {
    const configString = JSON.stringify(firebaseConfig);
    if (getApps().length === 0 || initializedConfigRef.current !== configString) {
      const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
      setAnalytics(getGoogleAnalytics(app));
      initializedConfigRef.current = configString;
    }
  }, [firebaseConfig, setAnalytics]);

  const getFirebaseToken = useCallback(async () => {
    const firebaseApp = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
    const messagingInstance = getMessaging(firebaseApp);
    setMessaging(messagingInstance);

    const getOrRegisterServiceWorker = () => {
      if ('serviceWorker' in navigator && typeof window.navigator.serviceWorker !== 'undefined') {
        return window.navigator.serviceWorker.getRegistration(firebaseScope).then(sw => {
          if (sw) {
            sw.update();
            return sw;
          }
          return window.navigator.serviceWorker.register(`/firebase-messaging-sw.js?${new URLSearchParams(firebaseConfig)}`, {
            scope: firebaseScope,
          });
        });
      }

      return null;
    };

    if (messagingInstance) {
      const serviceWorkerRegistration = await getOrRegisterServiceWorker();
      if (serviceWorkerRegistration) {
        setServiceWorker(serviceWorkerRegistration);
      }
    }
  }, [firebaseConfig, setMessaging, setServiceWorker]);

  useEffect(() => {
    if (useAuthenticationStore.getState().user?.externalId) {
      getFirebaseToken();
    }
  }, [getFirebaseToken]);

  useEffect(() => {
    if (serviceWorker?.active && 'serviceWorker' in navigator && messaging) {
      window.navigator.serviceWorker.getRegistration(firebaseScope).then(serviceWorkerRegistration => {
        if (serviceWorkerRegistration && env?.firebaseVapidKey && !isTokenRequestedRef.current) {
          isTokenRequestedRef.current = true;
          getToken(messaging, { serviceWorkerRegistration, vapidKey: env.firebaseVapidKey })
            .then((token: string) => updateFcmToken({ appVersion: version, fcmToken: token, platform: 'WEB' }))
            .catch(error => {
              isTokenRequestedRef.current = false;
              // eslint-disable-next-line no-console
              console.error('An error occurred while retrieving token. ', error);
            });
        }
      });

      const messageHandler = (
        event: MessageEvent<{
          type: string;
          url?: string;
        }>,
      ) => {
        // catch message send from firebase messaging sw when receiving background notifications
        if (event.origin === window.origin) {
          if (event.data?.type === 'PLAY_SOUND') {
            playSound();
          }

          if (event.data?.type === 'OPEN_ROOM' && event.data.url) {
            navigateRef.current(event.data?.url);
          }
        }
      };

      window.navigator.serviceWorker.addEventListener('message', messageHandler);

      return () => {
        window.navigator.serviceWorker.removeEventListener('message', messageHandler);
      };
    }
    return undefined;
  }, [env?.firebaseVapidKey, messaging, playSound, serviceWorker?.active]);

  useEffect(() => {
    if (messaging && serviceWorker) {
      return onMessage(messaging, payload => {
        const { data } = payload;
        const pushPayload = getPushNotificationPayload(data);
        const { selectedChannelId } = useChannelSessionStore.getState();

        const isViewingThisChannel = !!selectedChannelId && pushPayload?.channelId === selectedChannelId;

        if (pushPayload && (!isViewingThisChannel || !document.hasFocus())) {
          const openFlightRoomUrl = buildFlightRoomPushUrl(
            pushPayload.chatroomId,
            pushPayload.channelId,
            pushPayload.messageId,
            pushPayload.channelType,
            pushPayload.type,
          );

          playSound();
          serviceWorker
            .showNotification(pushPayload.title, {
              body: pushPayload.body,
              data: {
                ...pushPayload,
                url: openFlightRoomUrl,
              },
              icon: '/icons/favicon.ico',
              silent: false,
              tag: pushPayload.messageId,
            })
            .catch(error => {
              // eslint-disable-next-line no-console
              console.error('[push] foreground showNotification failed', error);
            });
        }
      });
    }
    return undefined;
  }, [messaging, serviceWorker, playSound]);

  useEffect(() => {
    const selectedFlightRoomId = useFlightRoomSessionStore.getState().selectedFlightRoomId;
    if (selectedFlightRoomId && serviceWorker?.active) {
      serviceWorker.active.postMessage({
        roomId: selectedFlightRoomId,
        type: 'CLEAR_NOTIFICATIONS',
      });
    }
  }, [serviceWorker?.active]);

  useEffect(() => {
    return useFlightRoomSessionStore.subscribe(state => {
      const selectedFlightRoomId = state.selectedFlightRoomId;
      if (selectedFlightRoomId && serviceWorker?.active) {
        serviceWorker.active.postMessage({
          roomId: selectedFlightRoomId,
          type: 'CLEAR_NOTIFICATIONS',
        });
      }
    });
  }, [serviceWorker?.active]);
};
